import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, Circle, X, Sparkles, ArrowRight } from "lucide-react";
import { toast } from "sonner";

/**
 * Dashboard onboarding checklist. Shows until user hits 100% or dismisses.
 * Also offers a one-click "Load Acme demo data" for fresh orgs.
 */
export default function OnboardingChecklist() {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/onboarding/status").then((r) => setState(r.data));
  useEffect(() => { load(); }, []);

  if (!state) return null;
  if (state.dismissed) return null;

  const { tasks, progress, demo_seeded } = state;
  const pct = Math.round((progress.done / progress.total) * 100);
  if (pct === 100) return null;

  const dismiss = async () => {
    await api.post("/onboarding/dismiss", {});
    setState({ ...state, dismissed: true });
  };

  const seed = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/onboarding/seed-demo", {});
      toast.success(`Loaded ${data.employees + data.records + data.sops} demo items`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-primary/30 bg-primary/[0.03] p-6 relative" data-testid="onboarding-checklist">
      <button
        onClick={dismiss}
        data-testid="onboarding-dismiss"
        className="absolute top-3 right-3 text-muted-foreground hover:text-foreground"
        aria-label="Dismiss checklist"
      >
        <X className="w-4 h-4" />
      </button>

      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="overline text-primary">/ GET STARTED</div>
          <h2 className="font-display text-2xl font-bold tracking-tight mt-1">
            Set up your workspace ({progress.done}/{progress.total})
          </h2>
          <div className="mt-3 h-1.5 rounded-full bg-border overflow-hidden max-w-md">
            <div className="h-full bg-primary transition-all duration-500" style={{ width: `${pct}%` }} />
          </div>
        </div>
        {!demo_seeded && (
          <button
            onClick={seed}
            disabled={busy}
            data-testid="load-demo-btn"
            className="border border-accent text-accent hover:bg-accent hover:text-white transition-colors rounded-sm px-4 py-2 text-sm font-medium inline-flex items-center gap-2"
          >
            <Sparkles className="w-4 h-4" />
            {busy ? "Loading…" : "Load Acme demo data"}
          </button>
        )}
      </div>

      <ul className="mt-6 grid grid-cols-1 lg:grid-cols-5 gap-3">
        {tasks.map((t) => (
          <Link
            to={t.url}
            key={t.id}
            data-testid={`checklist-task-${t.id}`}
            className={`border p-4 rounded-sm transition-colors ${
              t.done
                ? "border-emerald-200 bg-emerald-50/50 dark:border-emerald-900 dark:bg-emerald-950/20"
                : "border-border bg-card hover:border-primary"
            }`}
          >
            <div className="flex items-start gap-2">
              {t.done ? (
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
              ) : (
                <Circle className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
              )}
              <div className="min-w-0">
                <div className={`text-sm font-medium leading-tight ${t.done ? "text-muted-foreground" : ""}`}>
                  {t.title}
                </div>
                <div className="text-xs text-muted-foreground mt-1 leading-relaxed">{t.description}</div>
              </div>
            </div>
            {!t.done && (
              <div className="mt-3 text-xs font-mono-plex text-primary uppercase tracking-widest inline-flex items-center gap-1">
                Start <ArrowRight className="w-3 h-3" />
              </div>
            )}
          </Link>
        ))}
      </ul>
    </div>
  );
}
